import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import { active_coach_list } from "src/DAL/Menu/Menu";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const AVATAR_COLORS = [
  "#0f766e",
  "#0369a1",
  "#7c3aed",
  "#b45309",
  "#be123c",
  "#047857",
  "#4338ca",
];

export const coachDisplayName = (coach) =>
  `${coach?.first_name || ""} ${coach?.last_name || ""}`.trim() ||
  coach?.email ||
  "Coach";

export const coachInitials = (coach) => {
  const first = (coach?.first_name || "").trim();
  const last = (coach?.last_name || "").trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "C";
  }
  return (coach?.email || "C").charAt(0).toUpperCase();
};

export const coachAvatarColor = (coach) => {
  const key = String(coach?.id || coach?.email || coachDisplayName(coach));
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash];
};

export const normalizeCoachRef = (item) => {
  if (!item) return null;
  if (typeof item === "string") {
    return {
      id: item,
      first_name: item,
      last_name: "",
      email: "",
      permissionMode: "all",
      permissions: undefined,
    };
  }
  return {
    id: item.id || item._id,
    first_name: item.first_name || "",
    last_name: item.last_name || "",
    email: item.email || "",
    permissionMode: item.permissionMode === "specific" ? "specific" : "all",
    permissions:
      item.permissions && typeof item.permissions === "object"
        ? item.permissions
        : undefined,
  };
};

export default function AddCoachesPopup({
  open,
  selectedCoaches = [],
  onClose,
  onDone,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [roster, setRoster] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!open) return;

    setSearch("");
    setSelectedIds(
      (selectedCoaches || [])
        .map((item) => normalizeCoachRef(item)?.id)
        .filter(Boolean)
    );

    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const result = await active_coach_list();
        if (!active) return;
        if (result.code === 200) {
          const list = (result.coach || []).map((item) =>
            normalizeCoachRef(item)
          );
          setRoster(list.filter(Boolean));
        } else {
          enqueueSnackbar(result.message || "Could not load coaches", {
            variant: "error",
          });
          setRoster([]);
        }
      } catch (error) {
        if (active) {
          enqueueSnackbar("Could not load coaches", { variant: "error" });
          setRoster([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [open, enqueueSnackbar]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter((coach) => {
      const haystack = `${coachDisplayName(coach)} ${coach.email || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [roster, search]);

  const toggleCoach = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectAllShown = () => {
    const shownIds = filtered.map((coach) => coach.id);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...shownIds])));
  };

  const handleDone = () => {
    const byId = new Map(roster.map((coach) => [coach.id, coach]));
    // Keep previously selected coaches even if not in current roster response
    (selectedCoaches || []).forEach((item) => {
      const coach = normalizeCoachRef(item);
      if (coach?.id && !byId.has(coach.id)) byId.set(coach.id, coach);
    });

    const next = selectedIds
      .map((id) => byId.get(id))
      .filter(Boolean);

    onDone?.(next);
    onClose?.();
  };

  const selectedCount = selectedIds.length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
          Add coaches
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
          Search the roster and tick everyone who should pick up entries.
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder={`Search ${roster.length} coaches`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            {loading ? "Loading roster…" : `${roster.length} on the roster`}
          </Typography>
          <Typography
            component="button"
            type="button"
            onClick={selectAllShown}
            disabled={!filtered.length}
            sx={{
              border: 0,
              background: "none",
              p: 0,
              cursor: filtered.length ? "pointer" : "default",
              color: PRIMARY,
              fontSize: 13,
              fontWeight: 700,
              opacity: filtered.length ? 1 : 0.5,
            }}
          >
            Select all shown
          </Typography>
        </Box>

        <Box sx={{ maxHeight: 420, overflow: "auto", pr: 0.5 }}>
          {!loading && filtered.length === 0 ? (
            <Typography sx={{ fontSize: 14, color: MUTED, py: 3, textAlign: "center" }}>
              No coaches match your search.
            </Typography>
          ) : (
            filtered.map((coach) => {
              const checked = selectedIds.includes(coach.id);
              return (
                <Box
                  key={coach.id}
                  onClick={() => toggleCoach(coach.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    p: 1.25,
                    mb: 1,
                    borderRadius: "10px",
                    border: "1.5px solid",
                    borderColor: checked ? PRIMARY : BORDER,
                    bgcolor: checked ? PRIMARY_SOFT : "#fff",
                    cursor: "pointer",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => toggleCoach(coach.id)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{
                      color: PRIMARY,
                      "&.Mui-checked": { color: PRIMARY },
                    }}
                  />
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: 13,
                      fontWeight: 700,
                      bgcolor: coachAvatarColor(coach),
                    }}
                  >
                    {coachInitials(coach)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#0f172a",
                        lineHeight: 1.2,
                      }}
                    >
                      {coachDisplayName(coach)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: MUTED,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {coach.email || "—"}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: 13, color: MUTED }}>
          {selectedCount} coach{selectedCount === 1 ? "" : "es"} selected
        </Typography>
        <button
          type="button"
          className="small-contained-button"
          onClick={handleDone}
        >
          Done
        </button>
      </DialogActions>
    </Dialog>
  );
}
